import { useState, useEffect, useRef } from "react";
import { IStaff } from "@/interfaces/Istaff";
import { useUpdateStaff } from "@/api/staffApi";
import staffApi from "@/api/staffApi";

import toast from "react-hot-toast";
import { usePermissions } from "@/contexts/PermissionContext";
import { FiPlus, FiUser, FiClock } from "react-icons/fi";
import {
  FaInstagram,
  FaXTwitter,
  FaLinkedin,
  FaFacebook,
  FaYoutube,
} from "react-icons/fa6";
import BusinessHoursSelector from "@/components/BusinessHoursSelector";
import { IworkingHours } from "@/interfaces/Ibusiness";
import { ensurePhoneNumberFormat } from "@/utils/phoneNumberUtils";
import { businessLinesHooks, IBusinessLine } from "@/api/businessLinesApi";
interface EditStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: IStaff;
  onStaffUpdated: () => void;
}

// Status indicator for business lines
const getStatusIndicator = (status: string) => {
  const statusConfig = {
    active: {
      text: "Active",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    inactive: {
      text: "Inactive",
      color: "text-gray-600",
      bgColor: "bg-gray-100",
    },
    pending_activation: {
      text: "Pending",
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
    reserved: {
      text: "Reserved",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    suspended: {
      text: "Suspended",
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
  };

  const config =
    statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}
    >
      {config.text}
    </span>
  );
};

const PERMISSION_LEVELS = [
  {
    value: "no_access",
    label: "No access",
    description: "Unable to log in, profile managed by admin(s)",
  },
  {
    value: "standard",
    label: "Standard",
    description:
      "Able to view and manage their own profile, and view My Team, comms and settings (except billing)",
  },
  {
    value: "admin",
    label: "Admin",
    description:
      "Able to view and manage all profiles, comms and account settings",
  },
  {
    value: "owner",
    label: "Owner",
    description: "Full access to all features and settings, cannot be changed",
  },
];

// Custom Business Number Dropdown
const BusinessNumberDropdown = ({
  value,
  onChange,
  options,
  label = "Business number",
  disabled = false,
  showAllNumbers = false,
  loading = false,
  error = null,
  onRetry,
  multiple = false,
  selectedValues = [],
  onMultipleChange,
}: {
  value: string;
  onChange: (val: string) => void;
  options: IBusinessLine[];
  label?: string;
  disabled?: boolean;
  showAllNumbers?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  multiple?: boolean;
  selectedValues?: string[];
  onMultipleChange?: (values: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = multiple
    ? null
    : value === "all"
    ? { phone_number: "Business numbers" }
    : options.find((o) => o.id === value);

  const selectedCount = multiple ? selectedValues.length : 0;
  const displayText = multiple
    ? selectedCount === 0
      ? "Select business numbers"
      : selectedCount === 1
      ? options.find((o) => o.id === selectedValues[0])?.phone_number ||
        "1 number selected"
      : `${selectedCount} numbers selected`
    : selectedOption
    ? selectedOption.phone_number
    : "";

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (disabled || loading) {
    return (
      <div className="mb-2 relative">
        <div className="border rounded-lg px-4 py-2 bg-gray-50 flex flex-col">
          <span className="text-xs text-gray-400">{label}</span>
          <span className="text-lg text-gray-500 font-medium">
            {loading
              ? "Loading..."
              : showAllNumbers
              ? "All numbers"
              : "Not applicable"}
          </span>
          {disabled && !loading && (
            <span className="text-xs text-gray-400 mt-1">
              Select "Standard" permission level to choose business lines
            </span>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-2 relative">
        <div className="border rounded-lg px-4 py-2 bg-red-50 flex flex-col">
          <span className="text-xs text-red-400">{label}</span>
          <span className="text-lg text-red-500 font-medium">
            Error loading business lines
          </span>
          {onRetry && (
            <button
              className="text-xs text-red-600 hover:text-red-800 mt-1 text-left"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              Click to retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleMultipleSelection = (lineId: string) => {
    if (!onMultipleChange) return;

    const newSelection = selectedValues.includes(lineId)
      ? selectedValues.filter((id) => id !== lineId)
      : [...selectedValues, lineId];

    onMultipleChange(newSelection);
  };

  const handleSelectAll = () => {
    if (!onMultipleChange) return;
    const allIds = options.map((line) => line.id);
    onMultipleChange(allIds);
  };

  const handleDeselectAll = () => {
    if (!onMultipleChange) return;
    onMultipleChange([]);
  };

  return (
    <div className="mb-2 relative" ref={ref}>
      <div
        className={`border rounded-lg px-4 py-2 cursor-pointer bg-white transition-all flex flex-col focus:outline-none ${
          open
            ? "ring-2 ring-blue-200 border-blue-400"
            : "border-gray-200 hover:border-gray-300"
        }`}
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((v) => !v);
        }}
      >
        <span className="text-xs text-gray-400">{label}</span>
        <span className="text-lg text-gray-800 font-medium">{displayText}</span>
        {!multiple &&
          selectedOption &&
          "profile" in selectedOption &&
          selectedOption.profile?.business_name && (
            <span className="text-xs text-gray-500">
              {selectedOption.profile.business_name}
            </span>
          )}
        <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path stroke="#A0AEC0" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
          {multiple && (
            <>
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Select numbers
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                    >
                      Select all
                    </button>
                    <button
                      className="text-xs text-gray-600 hover:text-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeselectAll();
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {!multiple && (
            <div
              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                value === "all" ? "bg-gray-100 font-semibold" : ""
              }`}
              onClick={() => {
                onChange("all");
                setOpen(false);
              }}
            >
              Business numbers
            </div>
          )}

          {options.map((line) => {
            if (multiple) {
              const isSelected = selectedValues.includes(line.id);
              return (
                <div
                  key={line.id}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMultipleSelection(line.id);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by onClick
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      aria-label={`Select ${line.phone_number}${
                        line.profile.business_name
                          ? ` for ${line.profile.business_name}`
                          : ""
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{line.phone_number}</div>
                          {line.profile.business_name && (
                            <div className="text-xs text-gray-500">
                              {line.profile.business_name}
                            </div>
                          )}
                        </div>
                        <div className="ml-2">
                          {getStatusIndicator(line.status)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  key={line.id}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    value === line.id ? "bg-gray-100 font-semibold" : ""
                  }`}
                  onClick={() => {
                    onChange(line.id);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{line.phone_number}</div>
                      {line.profile.business_name && (
                        <div className="text-xs text-gray-500">
                          {line.profile.business_name}
                        </div>
                      )}
                    </div>
                    <div className="ml-2">
                      {getStatusIndicator(line.status)}
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}
    </div>
  );
};

export default function EditStaffModal({
  isOpen,
  onClose,
  staff,
  onStaffUpdated,
}: EditStaffModalProps) {
  const { permissionLevel: currentUserPermissionLevel } = usePermissions();
  const [businessNumbers, setBusinessNumbers] = useState<IBusinessLine[]>([]);
  const [activeTab, setActiveTab] = useState<"profile" | "workingHours">(
    "profile"
  );
  const [staffFields, setStaffFields] = useState({
    first_name: staff.profile.first_name,
    last_name: staff.profile.last_name,
    email: staff.email,
    permission_level: staff.permission_level,
    all_business_lines_permitted: staff.all_business_lines_permitted,
    permitted_business_lines: staff.permitted_business_lines,
    role: staff.profile.role || "",
    about: staff.profile.about || "",
    phones: staff.profile.phones?.length > 0 ? staff.profile.phones : [""],
    social_links: {
      website: staff.profile.social_links?.website || "",
      facebook: staff.profile.social_links?.facebook || "",
      twitter: staff.profile.social_links?.twitter || "",
      linkedin: staff.profile.social_links?.linkedin || "",
      instagram: staff.profile.social_links?.instagram || "",
      youtube: staff.profile.social_links?.youtube || "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Debug logs for state changes
  useEffect(() => {
    console.log(
      "🔄 Avatar file state changed:",
      avatarFile
        ? {
            name: avatarFile.name,
            size: avatarFile.size,
            type: avatarFile.type,
          }
        : null
    );
  }, [avatarFile]);

  useEffect(() => {
    console.log("🔄 Avatar preview state changed:", avatarPreview);
  }, [avatarPreview]);
  const [showFields, setShowFields] = useState<{
    phones: boolean;
    instagram: boolean;
    twitter: boolean;
    linkedin: boolean;
    facebook: boolean;
    youtube: boolean;
    website: boolean;
  }>({
    phones: Boolean(staff.profile.phones?.length),
    instagram: Boolean(staff.profile.social_links?.instagram),
    twitter: Boolean(staff.profile.social_links?.twitter),
    linkedin: Boolean(staff.profile.social_links?.linkedin),
    facebook: Boolean(staff.profile.social_links?.facebook),
    youtube: Boolean(staff.profile.social_links?.youtube),
    website: Boolean(staff.profile.social_links?.website),
  });
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Use the hook at component level
  const {
    data: businessLinesData,
    isLoading: businessLinesLoading,
    error: businessLinesError,
    refetch: refetchBusinessLines,
  } = businessLinesHooks.useActiveBusinessLines();
  const updateStaffMutation = useUpdateStaff();

  // Reset form fields when staff prop changes or modal opens
  useEffect(() => {
    console.log("🔄 Modal state changed:", { isOpen, hasStaff: !!staff });
    if (isOpen && staff) {
      console.log("👤 Staff data loaded:", {
        id: staff.id,
        email: staff.email,
        hasProfilePicture: !!staff.profile.picture,
        profilePicture: staff.profile.picture,
        firstName: staff.profile.first_name,
        lastName: staff.profile.last_name,
      });
      setStaffFields({
        first_name: staff.profile.first_name,
        last_name: staff.profile.last_name,
        email: staff.email,
        permission_level: staff.permission_level,
        all_business_lines_permitted: staff.all_business_lines_permitted,
        permitted_business_lines: staff.permitted_business_lines,
        role: staff.profile.role || "",
        about: staff.profile.about || "",
        phones: staff.profile.phones?.length > 0 ? staff.profile.phones : [""],
        social_links: {
          website: staff.profile.social_links?.website || "",
          facebook: staff.profile.social_links?.facebook || "",
          twitter: staff.profile.social_links?.twitter || "",
          linkedin: staff.profile.social_links?.linkedin || "",
          instagram: staff.profile.social_links?.instagram || "",
          youtube: staff.profile.social_links?.youtube || "",
        },
      });

      setShowFields({
        phones: Boolean(staff.profile.phones?.length),
        instagram: Boolean(staff.profile.social_links?.instagram),
        twitter: Boolean(staff.profile.social_links?.twitter),
        linkedin: Boolean(staff.profile.social_links?.linkedin),
        facebook: Boolean(staff.profile.social_links?.facebook),
        youtube: Boolean(staff.profile.social_links?.youtube),
        website: Boolean(staff.profile.social_links?.website),
      });

      // Reset other states
      setAvatarFile(null);
      setAvatarPreview(null);
      setActiveTab("profile");
      setShowAddMenu(false);
    }
  }, [isOpen, staff]);

  // Update local state when data changes
  useEffect(() => {
    if (businessLinesData) {
      let filteredLines = businessLinesData;

      // Always hide pending_activation and reserved numbers
      filteredLines = businessLinesData.filter(
        (line: IBusinessLine) =>
          line.status !== "pending_activation" && line.status !== "reserved"
      );

      // If current user is owner and editing an admin, only show active and inactive lines
      if (
        currentUserPermissionLevel === "owner" &&
        staffFields.permission_level === "admin"
      ) {
        filteredLines = filteredLines.filter(
          (line: IBusinessLine) =>
            line.status === "active" || line.status === "inactive"
        );
      }

      setBusinessNumbers(filteredLines);
    }
  }, [
    businessLinesData,
    currentUserPermissionLevel,
    staffFields.permission_level,
  ]);

  // Function to retry fetching business lines
  const fetchBusinessNumbers = async () => {
    try {
      await refetchBusinessLines();
    } catch (error) {
      console.error("Error fetching business lines:", error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showAddMenu && !target.closest(".add-menu-container")) {
        setShowAddMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddMenu]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("🖼️ Avatar change triggered", e.target.files);

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log("📁 File selected:", {
        name: file.name,
        type: file.type,
        size: file.size,
        sizeInMB: (file.size / (1024 * 1024)).toFixed(2),
      });

      // Validate file type
      if (!file.type.startsWith("image/")) {
        console.log("❌ Invalid file type:", file.type);
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        console.log("❌ File too large:", file.size);
        toast.error("Image size should be less than 5MB");
        return;
      }

      console.log("✅ File validation passed, setting avatar file and preview");
      setAvatarFile(file);
      const previewUrl = URL.createObjectURL(file);
      console.log("🖼️ Preview URL created:", previewUrl);
      setAvatarPreview(previewUrl);
    } else {
      console.log("❌ No file selected");
    }
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleFieldChange = (field: string, value: any) => {
    if (field === "permission_level") {
      // If changing to admin, set all_business_lines_permitted to true
      if (value === "admin") {
        setStaffFields((prev) => ({
          ...prev,
          [field]: value,
          all_business_lines_permitted: true,
          permitted_business_lines: [],
        }));
      } else if (value === "standard") {
        // If changing to standard, reset business line permissions
        setStaffFields((prev) => ({
          ...prev,
          [field]: value,
          all_business_lines_permitted: false,
          permitted_business_lines: [],
        }));
      } else {
        setStaffFields((prev) => ({ ...prev, [field]: value }));
      }
    } else if (field === "social_links") {
      setStaffFields((prev) => ({
        ...prev,
        social_links: { ...prev.social_links, ...value },
      }));
    } else {
      setStaffFields((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handlePhoneChange = (index: number, value: string) => {
    setStaffFields((prev) => ({
      ...prev,
      phones: prev.phones.map((p, i) => (i === index ? value : p)),
    }));
  };

  const handleRemovePhone = (index: number) => {
    setStaffFields((prev) => {
      const newPhones = prev.phones.filter((_, i) => i !== index);
      // If removing this phone would leave us with no phones, hide the field
      if (newPhones.length === 0) {
        setShowFields((prevShow) => ({ ...prevShow, phones: false }));
      }
      return {
        ...prev,
        phones: newPhones,
      };
    });
  };

  const handleAddField = (field: string) => {
    setShowAddMenu(false);
    setShowFields((prev) => ({ ...prev, [field]: true }));
    if (field === "phones") {
      setStaffFields((prev) => ({
        ...prev,
        phones: [...prev.phones, ""],
      }));
    }
  };

  const handleRemoveSocial = (field: string) => {
    setShowFields((prev) => ({ ...prev, [field]: false }));
    setStaffFields((prev) => ({
      ...prev,
      social_links: { ...prev.social_links, [field]: "" },
    }));
  };

  const handleWorkingHoursSave = async (
    workingHours: any,
    timezone?: string
  ) => {
    try {
      await updateStaffMutation.mutateAsync({
        id: staff.id,
        staff: {
          ...staff,
          // Keep original email - it should never be changed
          email: staff.email,
          profile: {
            ...staff.profile,
            working_hours: workingHours,
            ...(timezone && { timezone }),
          },
        },
      });
      toast.success("Working hours updated successfully");
    } catch (error) {
      console.error("Error updating working hours:", error);
      toast.error("Failed to update working hours");
      throw error;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Basic validation
      if (!staffFields.first_name && !staffFields.last_name) {
        toast.error("Please enter at least a first name or last name");
        setLoading(false);
        return;
      }

      // Validate business lines for standard permission level
      if (
        staffFields.permission_level === "standard" &&
        !staffFields.all_business_lines_permitted &&
        staffFields.permitted_business_lines.length === 0
      ) {
        toast.error(
          "Please select at least one business line for standard permission level"
        );
        setLoading(false);
        return;
      }

      // Format all phone numbers with +1 if not present
      const formattedPhones = staffFields.phones
        .filter((p) => p.trim() !== "")
        .map((p) => ensurePhoneNumberFormat(p));

      let pictureUrl = null;

      // If there's a new avatar file, upload it first
      if (avatarFile) {
        console.log("📤 Avatar file detected, attempting upload:", {
          fileName: avatarFile.name,
          fileSize: avatarFile.size,
          fileType: avatarFile.type,
        });

        try {
          console.log("📤 Uploading picture to server...");
          const pictureResponse = await staffApi.uploadPicture(
            staff.id,
            avatarFile
          );
          console.log(
            "✅ Picture uploaded successfully:",
            pictureResponse.data
          );
          pictureUrl = pictureResponse.data.picture;
          toast.success("Avatar uploaded successfully");
        } catch (error) {
          console.error("❌ Error uploading staff picture:", error);
          toast.error("Failed to upload picture");
          setLoading(false);
          return;
        }
      } else {
        console.log("ℹ️ No avatar file to upload");
      }

      await updateStaffMutation.mutateAsync({
        id: staff.id,
        staff: {
          ...staff,
          // Keep original email - it should never be changed
          email: staff.email,
          // Keep original permission level if it's owner
          permission_level:
            staff.permission_level === "owner"
              ? staff.permission_level
              : staffFields.permission_level,
          all_business_lines_permitted:
            staff.permission_level === "owner"
              ? staff.all_business_lines_permitted
              : staffFields.all_business_lines_permitted,
          permitted_business_lines:
            staff.permission_level === "owner"
              ? staff.permitted_business_lines
              : staffFields.permitted_business_lines,
          profile: {
            ...staff.profile,
            first_name: staffFields.first_name,
            last_name: staffFields.last_name,
            role: staffFields.role,
            about: staffFields.about,
            phones: formattedPhones,
            social_links: {
              website: staffFields.social_links.website || null,
              facebook: staffFields.social_links.facebook || null,
              twitter: staffFields.social_links.twitter || null,
              linkedin: staffFields.social_links.linkedin || null,
              instagram: staffFields.social_links.instagram || null,
              youtube: staffFields.social_links.youtube || null,
            },
            picture: pictureUrl || staff.profile.picture,
          },
        },
      });

      toast.success("Staff member updated successfully");
      onStaffUpdated();
      onClose();
    } catch (error) {
      console.error("Error updating staff:", error);
      toast.error("Failed to update staff member.");
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = () => {
    return (
      `${staffFields.first_name || ""} ${staffFields.last_name || ""}`.trim() ||
      "Full Name"
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black opacity-30"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative w-full max-w-5xl rounded-lg bg-white flex flex-col md:flex-row shadow-lg">
          {/* Modal Title */}
          <div className="absolute top-0 left-0 w-full flex items-center justify-between px-8 py-6 border-b border-gray-200 bg-white rounded-t-lg z-10">
            <h2 className="text-xl font-semibold">Edit staff</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          {/* Sidebar */}
          <div className="flex flex-col items-center w-72 bg-white border-r border-gray-200 pt-24 pb-8 px-6 flex-shrink-0 relative">
            {/* Avatar and Name */}
            <div className="flex flex-col items-center w-full mb-8">
              <div className="w-20 h-20 rounded-full bg-[#78C7D1] flex items-center justify-center overflow-hidden relative mb-3">
                {(() => {
                  const staffWithBlob = staff as any; // Type assertion for pictureBlob
                  console.log("🖼️ Avatar render check:", {
                    hasAvatarPreview: !!avatarPreview,
                    hasStaffPictureBlob: !!staffWithBlob.profile.pictureBlob,
                    hasStaffPicture: !!staff.profile.picture,
                    avatarPreview: avatarPreview,
                    staffPictureBlob: staffWithBlob.profile.pictureBlob,
                    staffPicture: staff.profile.picture,
                    displayName: getDisplayName(),
                  });

                  if (avatarPreview) {
                    return (
                      <img
                        src={avatarPreview}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        onLoad={() =>
                          console.log("✅ Avatar preview image loaded")
                        }
                        onError={(e) => {
                          console.log(
                            "❌ Avatar preview image failed to load:",
                            e
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    );
                  } else if (staffWithBlob.profile.pictureBlob) {
                    return (
                      <img
                        src={staffWithBlob.profile.pictureBlob}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        onLoad={() =>
                          console.log("✅ Staff profile pictureBlob loaded")
                        }
                        onError={(e) => {
                          console.log(
                            "❌ Staff profile pictureBlob failed to load:",
                            e
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    );
                  } else if (staff.profile.picture) {
                    return (
                      <img
                        src={staff.profile.picture}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        onLoad={() =>
                          console.log("✅ Staff profile image loaded")
                        }
                        onError={(e) => {
                          console.log(
                            "❌ Staff profile image failed to load:",
                            e
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    );
                  } else {
                    return (
                      <span className="text-3xl text-white font-bold">
                        {getDisplayName().charAt(0) || "M"}
                      </span>
                    );
                  }
                })()}
                {loading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <div className="text-base font-semibold text-gray-800 text-center">
                {getDisplayName()}
              </div>
              <input
                type="file"
                accept="image/*"
                id="avatar-upload"
                className="hidden"
                onChange={handleAvatarChange}
                disabled={loading}
                onClick={() => console.log("🖱️ File input clicked")}
              />
              <label
                htmlFor="avatar-upload"
                className={`mt-2 inline-block cursor-pointer rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => console.log("🖱️ Upload label clicked")}
              >
                {avatarFile ? "Change" : "Upload"} Avatar
              </label>
            </div>
            {/* Tabs */}
            <nav className="flex flex-col w-full gap-2 mt-2">
              <button
                onClick={() => setActiveTab("profile")}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg text-base font-medium transition-colors text-left ${
                  activeTab === "profile"
                    ? "bg-gray-100 text-gray-900 font-semibold"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <FiUser
                  size={20}
                  className={
                    activeTab === "profile" ? "text-gray-900" : "text-gray-400"
                  }
                />
                Profile
              </button>
              <button
                onClick={() => setActiveTab("workingHours")}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg text-base font-medium transition-colors text-left ${
                  activeTab === "workingHours"
                    ? "bg-gray-100 text-gray-900 font-semibold"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <FiClock
                  size={20}
                  className={
                    activeTab === "workingHours"
                      ? "text-gray-900"
                      : "text-gray-400"
                  }
                />
                Working hours
              </button>
            </nav>
            {/* Add Menu - Only show in Profile tab */}
            {activeTab === "profile" && (
              <div className="mt-auto pt-8 flex items-center relative add-menu-container w-full justify-center">
                <button
                  type="button"
                  className="flex items-center text-green-600 border border-green-600 rounded px-3 py-1 hover:bg-green-50"
                  onClick={() => setShowAddMenu((prev) => !prev)}
                >
                  <FiPlus className="mr-1" /> Add
                </button>
                {showAddMenu && (
                  <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border rounded shadow-lg p-2 flex flex-col min-w-[180px]">
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("phones")}
                      disabled={showFields.phones}
                    >
                      <FiPlus className="mr-2" /> Phone
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("instagram")}
                      disabled={showFields.instagram}
                    >
                      <FaInstagram className="mr-2" /> Instagram
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("twitter")}
                      disabled={showFields.twitter}
                    >
                      <FaXTwitter className="mr-2" /> X
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("linkedin")}
                      disabled={showFields.linkedin}
                    >
                      <FaLinkedin className="mr-2" /> LinkedIn
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("facebook")}
                      disabled={showFields.facebook}
                    >
                      <FaFacebook className="mr-2" /> Facebook
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("youtube")}
                      disabled={showFields.youtube}
                    >
                      <FaYoutube className="mr-2" /> YouTube
                    </button>
                    <button
                      className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => handleAddField("website")}
                      disabled={showFields.website}
                    >
                      <FiPlus className="mr-2" /> Website
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Main Content */}
          <div className="flex-1 w-full md:w-2/3 pl-0 px-8 py-16">
            {activeTab === "profile" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={staffFields.first_name}
                    onChange={(e) =>
                      handleFieldChange("first_name", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={staffFields.last_name}
                    onChange={(e) =>
                      handleFieldChange("last_name", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Last name"
                  />
                </div>
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Primary Email{" "}
                    <span className="text-xs text-gray-500">(Locked)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={staffFields.email}
                      readOnly
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm bg-gray-100 text-gray-700 cursor-not-allowed"
                      placeholder="Email address"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-gray-400 text-sm">🔒</span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Primary email cannot be changed or deleted
                  </p>
                </div>
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <input
                    type="text"
                    value={staffFields.role}
                    onChange={(e) => handleFieldChange("role", e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Role or position"
                  />
                </div>
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Permission Level{" "}
                    {staffFields.permission_level === "owner" && (
                      <span className="text-xs text-gray-500">(Locked)</span>
                    )}
                  </label>
                  {staffFields.permission_level === "owner" ? (
                    <div className="relative">
                      <input
                        type="text"
                        value="Owner"
                        readOnly
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm bg-gray-100 text-gray-700 cursor-not-allowed"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-gray-400 text-sm">🔒</span>
                      </div>
                    </div>
                  ) : (
                    <select
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      value={staffFields.permission_level}
                      onChange={(e) =>
                        handleFieldChange("permission_level", e.target.value)
                      }
                    >
                      {PERMISSION_LEVELS.filter(
                        (level) => level.value !== "owner"
                      ).map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {
                      PERMISSION_LEVELS.find(
                        (l) => l.value === staffFields.permission_level
                      )?.description
                    }
                  </p>
                  {staffFields.permission_level === "owner" && (
                    <p className="mt-1 text-xs text-amber-600">
                      Owner permission level cannot be changed
                    </p>
                  )}
                </div>
                <div className="col-span-1 md:col-span-2">
                  <BusinessNumberDropdown
                    value={
                      staffFields.all_business_lines_permitted
                        ? "all"
                        : staffFields.permitted_business_lines[0] || ""
                    }
                    onChange={(val) => {
                      if (val === "all") {
                        handleFieldChange("all_business_lines_permitted", true);
                        handleFieldChange("permitted_business_lines", []);
                      } else {
                        handleFieldChange(
                          "all_business_lines_permitted",
                          false
                        );
                        handleFieldChange("permitted_business_lines", [val]);
                      }
                    }}
                    options={businessNumbers}
                    label="Business Numbers Access"
                    disabled={
                      staffFields.permission_level === "no_access" ||
                      staffFields.permission_level === "admin" ||
                      staffFields.permission_level === "owner"
                    }
                    showAllNumbers={staffFields.permission_level === "admin"}
                    loading={businessLinesLoading}
                    error={
                      businessLinesError
                        ? "Failed to load business lines"
                        : null
                    }
                    onRetry={fetchBusinessNumbers}
                    multiple={staffFields.permission_level === "standard"}
                    selectedValues={staffFields.permitted_business_lines}
                    onMultipleChange={(values) => {
                      handleFieldChange("permitted_business_lines", values);
                      // Always set all_business_lines_permitted to false when selecting specific lines
                      handleFieldChange("all_business_lines_permitted", false);
                    }}
                  />
                  {staffFields.permission_level === "standard" &&
                    staffFields.permitted_business_lines.length === 0 && (
                      <p className="mt-1 text-xs text-amber-600">
                        ⚠️ At least one business line must be selected for
                        standard permission level
                      </p>
                    )}
                </div>

                {/* Phones */}
                {showFields.phones && (
                  <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Phone(s)
                    </label>
                    {staffFields.phones.map((phone, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-1">
                        <div className="w-28">
                          <input
                            type="text"
                            value="+1"
                            readOnly
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                          />
                        </div>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) =>
                            handlePhoneChange(idx, e.target.value)
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="(123) 456-7890"
                        />
                        {staffFields.phones.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemovePhone(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            &#x2715;
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setStaffFields((prev) => ({
                          ...prev,
                          phones: [...prev.phones, ""],
                        }))
                      }
                      className="text-blue-600 hover:underline text-sm w-fit"
                    >
                      + Add Phone
                    </button>
                  </div>
                )}

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <input
                    type="text"
                    value={staffFields.social_links.website}
                    onChange={(e) =>
                      handleFieldChange("social_links", {
                        website: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter website URL"
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Social Links
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {/* Social Links */}
                    {showFields.instagram && (
                      <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                        <FaInstagram className="text-pink-500" />
                        <input
                          type="text"
                          value={staffFields.social_links.instagram}
                          onChange={(e) =>
                            handleFieldChange("social_links", {
                              instagram: e.target.value,
                            })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Instagram"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSocial("instagram")}
                          className="text-red-500 hover:text-red-700"
                        >
                          &#x2715;
                        </button>
                      </div>
                    )}
                    {showFields.twitter && (
                      <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                        <FaXTwitter className="text-black" />
                        <input
                          type="text"
                          value={staffFields.social_links.twitter}
                          onChange={(e) =>
                            handleFieldChange("social_links", {
                              twitter: e.target.value,
                            })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="X (Twitter)"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSocial("twitter")}
                          className="text-red-500 hover:text-red-700"
                        >
                          &#x2715;
                        </button>
                      </div>
                    )}
                    {showFields.linkedin && (
                      <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                        <FaLinkedin className="text-blue-700" />
                        <input
                          type="text"
                          value={staffFields.social_links.linkedin}
                          onChange={(e) =>
                            handleFieldChange("social_links", {
                              linkedin: e.target.value,
                            })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="LinkedIn"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSocial("linkedin")}
                          className="text-red-500 hover:text-red-700"
                        >
                          &#x2715;
                        </button>
                      </div>
                    )}
                    {showFields.facebook && (
                      <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                        <FaFacebook className="text-blue-600" />
                        <input
                          type="text"
                          value={staffFields.social_links.facebook}
                          onChange={(e) =>
                            handleFieldChange("social_links", {
                              facebook: e.target.value,
                            })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Facebook"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSocial("facebook")}
                          className="text-red-500 hover:text-red-700"
                        >
                          &#x2715;
                        </button>
                      </div>
                    )}
                    {showFields.youtube && (
                      <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                        <FaYoutube className="text-red-600" />
                        <input
                          type="text"
                          value={staffFields.social_links.youtube}
                          onChange={(e) =>
                            handleFieldChange("social_links", {
                              youtube: e.target.value,
                            })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="YouTube"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSocial("youtube")}
                          className="text-red-500 hover:text-red-700"
                        >
                          &#x2715;
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    About
                  </label>
                  <textarea
                    value={staffFields.about}
                    onChange={(e) => handleFieldChange("about", e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Write about this team member"
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              /* Working Hours Tab */
              <div className="w-full p-8">
                <BusinessHoursSelector
                  initialWorkingHours={
                    staff.profile.working_hours as unknown as IworkingHours
                  }
                  initialTimezone={staff.profile.timezone}
                  onSave={handleWorkingHoursSave}
                  isLoading={loading}
                  className=""
                />
              </div>
            )}

            {/* Footer - Only show in Profile tab */}
            {activeTab === "profile" && (
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={`rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
